import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  name?: string;
  'data-testid'?: string;
}

interface DropdownPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  openUp: boolean;
}

export function CustomSelect({
  value,
  onChange,
  options,
  placeholder = '',
  required,
  disabled,
  className,
  name,
  'data-testid': testId,
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [position, setPosition] = useState<DropdownPosition>({ left: 0, width: 0, openUp: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  // Compute dropdown position from trigger button
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 6;
    const maxDropdownHeight = 240; // max-h-60 = 15rem = 240px
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow;

    if (openUp) {
      setPosition({
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: rect.width,
        openUp: true,
      });
    } else {
      setPosition({
        top: rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        openUp: false,
      });
    }
  }, []);

  // Update position when opening and on scroll/resize
  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();

    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [open, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        listRef.current &&
        !listRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Scroll focused item into view
  useEffect(() => {
    if (!open || focusIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[focusIndex] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [focusIndex, open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (!open) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setOpen(true);
          const idx = options.findIndex((o) => o.value === value);
          setFocusIndex(idx >= 0 ? idx : 0);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusIndex((prev) => {
            let next = prev + 1;
            while (next < options.length && options[next].disabled) next++;
            return next < options.length ? next : prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusIndex((prev) => {
            let next = prev - 1;
            while (next >= 0 && options[next].disabled) next--;
            return next >= 0 ? next : prev;
          });
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusIndex >= 0 && !options[focusIndex].disabled) {
            onChange(options[focusIndex].value);
            setOpen(false);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
      }
    },
    [open, focusIndex, options, value, onChange, disabled]
  );

  const dropdown = open
    ? createPortal(
        <ul
          ref={listRef}
          role="listbox"
          style={{
            position: 'fixed',
            ...(position.openUp ? { bottom: position.bottom } : { top: position.top }),
            left: position.left,
            width: position.width,
            zIndex: 99999,
          }}
          className={cn(
            'overflow-auto rounded-xl',
            'bg-white dark:bg-gray-800',
            'border border-gray-200 dark:border-gray-600',
            'shadow-xl shadow-black/10 dark:shadow-black/30',
            'max-h-60 py-1'
          )}
        >
          {options.map((option, index) => {
            const showGroupHeader =
              option.group && (index === 0 || options[index - 1].group !== option.group);
            return (
              <li key={option.value}>
                {showGroupHeader && (
                  <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mt-1 first:mt-0">
                    {option.group}
                  </div>
                )}
                <div
                  role="option"
                  aria-selected={option.value === value}
                  data-focused={index === focusIndex}
                  onClick={() => {
                    if (!option.disabled) {
                      onChange(option.value);
                      setOpen(false);
                    }
                  }}
                  onMouseEnter={() => setFocusIndex(index)}
                  className={cn(
                    'flex cursor-pointer items-center justify-between px-4 py-2.5 text-sm transition-colors',
                    option.value === value
                      ? 'bg-[#16c47f]/10 text-[#16c47f] dark:bg-[#16c47f]/20 font-medium'
                      : 'text-gray-700 dark:text-gray-200',
                    index === focusIndex &&
                      option.value !== value &&
                      'bg-gray-50 dark:bg-gray-700/50',
                    option.disabled && 'opacity-40 cursor-not-allowed',
                    option.group && 'pl-6'
                  )}
                >
                  <span className="truncate">{option.label}</span>
                  {option.value === value && <Check className="h-4 w-4 shrink-0 text-[#16c47f]" />}
                </div>
              </li>
            );
          })}
          {options.length === 0 && (
            <li className="px-4 py-3 text-center text-sm text-gray-400 dark:text-gray-500">
              Aucune option
            </li>
          )}
        </ul>,
        document.body
      )
    : null;

  return (
    <div className="relative" onKeyDown={handleKeyDown}>
      {/* Hidden native input for form validation */}
      {required && (
        <input
          tabIndex={-1}
          name={name}
          value={value}
          onChange={() => {}}
          required
          className="absolute inset-0 opacity-0 pointer-events-none"
          aria-hidden="true"
        />
      )}

      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        data-testid={testId}
        onClick={() => {
          if (!disabled) {
            if (!open) {
              // Compute position synchronously before opening to avoid flicker
              updatePosition();
              const idx = options.findIndex((o) => o.value === value);
              setFocusIndex(idx >= 0 ? idx : 0);
            }
            setOpen(!open);
          }
        }}
        className={cn(
          'flex w-full items-center justify-between px-4 py-3 text-left',
          'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl',
          'text-gray-900 dark:text-white text-sm',
          'focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]',
          'transition-all duration-150',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          open && 'ring-2 ring-[#16c47f]/30 border-[#16c47f]',
          className
        )}
      >
        <span className={cn(!selectedOption && 'text-gray-400 dark:text-gray-500')}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {dropdown}
    </div>
  );
}
